<script setup lang="ts">
// The admins queue table. Row click opens the detail drawer; the kebab
// column owns the per-row mutations (edit / suspend / reset MFA / resend
// invite / view audit / remove). Bulk select intentionally absent — the
// roster is small and the queue isn't list-action heavy.
//
// Chrome (header / row density / risk highlights / paginator) comes from
// the kit DataTable element via its `row-class` API + the `row--super` /
// `row--inactive` / `row--suspended` / `row--mfa-off` hooks. The only
// local CSS in this file is the small operator-meta affordances (avatar
// ring, super-admin pin, last-login nowrap) — every visual that should
// stay consistent across queues lives in the kit.

import { computed, ref } from 'vue'

import DataTable, { type DataTableColumn } from '@uiKit/elements/DataTable.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Menu from '@uiKit/elements/Menu.vue'
import Chip from '@uiKit/elements/Chip.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import { formatDateTime, getInitials } from '@shared/utils'

import type { Admin, AdminStatus, InvitationStatus } from '../types/adminTypes'

interface Props {
  admins: Admin[]
  isLoading: boolean
  page: number
  limit: number
  totalItems: number
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'select', admin: Admin): void
  (e: 'edit', admin: Admin): void
  (e: 'remove', admin: Admin): void
  (e: 'suspend', admin: Admin): void
  (e: 'resetMfa', admin: Admin): void
  (e: 'resendInvite', admin: Admin): void
  (e: 'viewAudit', admin: Admin): void
  (e: 'page-change', payload: { page: number; limit: number }): void
}>()

const showPaginator = computed(() => props.totalItems > props.limit)

// ── Column setup ────────────────────────────────────────────────────────

const columns: DataTableColumn[] = [
  { field: 'name', header: 'Operator', minWidth: '280px', align: 'left' },
  { field: 'role', header: 'Role', width: '170px', align: 'left' },
  { field: 'status', header: 'Status', width: '180px', align: 'left' },
  { field: 'lastLoginAt', header: 'Last login', width: '200px', align: 'right' },
  { field: 'mfaEnabled', header: 'MFA', width: '150px', align: 'center' },
  { field: 'actions', header: '', width: '64px', align: 'right', sortable: false },
]

// ── Visual lookup tables ─────────────────────────────────────────────────

type ChipColor = 'success' | 'warning' | 'danger' | 'info' | 'neutral'

const STATUS_COLOR: Record<AdminStatus, ChipColor> = {
  Active: 'success',
  Inactive: 'neutral',
  Suspended: 'danger',
}

const INVITATION_LABEL: Record<InvitationStatus, string> = {
  invited: 'Invite pending',
  accepted: 'Accepted',
  expired: 'Invite expired',
  revoked: 'Invite revoked',
}

const INVITATION_COLOR: Record<InvitationStatus, ChipColor> = {
  invited: 'info',
  accepted: 'neutral',
  expired: 'warning',
  revoked: 'danger',
}

const INVITATION_ICON: Record<InvitationStatus, 'envelope' | 'clock' | 'times-circle' | 'check'> = {
  invited: 'envelope',
  accepted: 'check',
  expired: 'clock',
  revoked: 'times-circle',
}

const initials = (name: string) => getInitials(...name.split(' ').slice(0, 2))

// ── Kebab menu ─────────────────────────────────────────────────────────

const menuRef = ref<InstanceType<typeof Menu> | null>(null)
const menuTarget = ref<Admin | null>(null)

const menuModel = ref([
  {
    label: 'View details',
    icon: 'pi pi-eye',
    command: () => menuTarget.value && emit('select', menuTarget.value),
  },
  {
    label: 'Edit permissions',
    icon: 'pi pi-pencil',
    command: () => menuTarget.value && emit('edit', menuTarget.value),
  },
  {
    label: 'Resend invite',
    icon: 'pi pi-envelope',
    command: () => menuTarget.value && emit('resendInvite', menuTarget.value),
  },
  {
    label: 'Reset MFA',
    icon: 'pi pi-shield',
    command: () => menuTarget.value && emit('resetMfa', menuTarget.value),
  },
  {
    label: 'View audit log',
    icon: 'pi pi-history',
    command: () => menuTarget.value && emit('viewAudit', menuTarget.value),
  },
  { separator: true },
  {
    label: 'Suspend',
    icon: 'pi pi-ban',
    command: () => menuTarget.value && emit('suspend', menuTarget.value),
  },
  {
    label: 'Remove',
    icon: 'pi pi-trash',
    command: () => menuTarget.value && emit('remove', menuTarget.value),
  },
])

function openMenu(event: Event, admin: Admin) {
  menuTarget.value = admin
  const target = (event.currentTarget ??
    (event.target as HTMLElement)?.closest('button')) as HTMLElement | null
  menuRef.value?.show(event, target ?? undefined)
}
</script>

<template>
  <Box>
    <DataTable
      :value="admins"
      :is-loading="isLoading"
      :columns="columns"
      :lazy="true"
      :page="page"
      :limit="limit"
      :total-items="totalItems"
      :paginator="showPaginator"
      row-hover
      @row-click="(e: { data: Admin; originalEvent: Event }) => emit('select', e.data)"
      @change="emit('page-change', $event)"
    >
      <template #name="{ data }">
        <Box flex align="center" gap="3">
          <Box class="avatar" flex align="center" justify="center">
            <Text is="span" type="caption" weight="semibold" color="inverse">{{
              initials(data.name)
            }}</Text>
          </Box>
          <Box flex direction="col" gap="1" class="operator-meta">
            <Box flex align="center" gap="2">
              <Text type="body" weight="semibold">{{ data.name }}</Text>
              <Box
                v-if="data.role === 'Super Admin'"
                flex
                align="center"
                justify="center"
                class="super-pin"
                aria-label="Super admin"
              >
                <PiIcon name="shield" />
              </Box>
            </Box>
            <Text type="caption" color="muted">{{ data.email }}</Text>
          </Box>
        </Box>
      </template>

      <template #role="{ data }">
        <Text type="body-sm" weight="medium">{{ data.role }}</Text>
      </template>

      <template #status="{ data }">
        <Box flex direction="col" gap="1" align="start">
          <Chip
            :label="data.status"
            :color="STATUS_COLOR[data.status as AdminStatus]"
            variant="tonal"
            size="small"
            dot
          />
          <Chip
            v-if="data.invitationStatus !== 'accepted'"
            :label="INVITATION_LABEL[data.invitationStatus as InvitationStatus]"
            :color="INVITATION_COLOR[data.invitationStatus as InvitationStatus]"
            :icon="INVITATION_ICON[data.invitationStatus as InvitationStatus]"
            variant="tonal"
            size="small"
          />
        </Box>
      </template>

      <template #lastLoginAt="{ data }">
        <Text type="body-sm" :color="data.lastLoginAt ? 'default' : 'muted'" class="last-login">
          {{ data.lastLoginAt ? formatDateTime(data.lastLoginAt) : 'Never' }}
        </Text>
      </template>

      <template #mfaEnabled="{ data }">
        <Chip
          :label="data.mfaEnabled ? 'Enabled' : 'Disabled'"
          :color="data.mfaEnabled ? 'success' : 'warning'"
          :icon="data.mfaEnabled ? 'shield' : 'exclamation-triangle'"
          variant="tonal"
          size="small"
        />
      </template>

      <template #actions="{ data }">
        <Button
          icon="ellipsis-v"
          variant="text"
          size="small"
          class="action-kebab"
          aria-label="Row actions"
          @click.stop="openMenu($event, data)"
        />
      </template>

      <template #empty>
        <Box flex direction="col" align="center" class="empty">
          <Text type="body" color="muted">No sub-admin users yet.</Text>
        </Box>
      </template>
    </DataTable>

    <Menu ref="menuRef" :model="menuModel" popup />
  </Box>
</template>

<style scoped>
/* ── Operator-specific affordances ─────────────────────────────────────
   Everything queue-generic (table chrome, row risk highlights, paginator)
   lives in @uiKit/elements/DataTable.vue and propagates to every screen.
   This file owns only what's specific to the "operator" cell shape.
*/

/* Option 2 avatar — premium gradient base + 2px white ring + soft drop
   shadow. Reads as institutional identity, not flat consumer monogram.
   Uniform across roles; role hierarchy lives in chips + the shield pin. */
.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    var(--p-sliceal-dark-slate) 0%,
    var(--p-sliceal-dark-slate-hover) 100%
  );
  flex-shrink: 0;
  border: 2px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.08);
}

.super-pin {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background: var(--p-primary-500);
  color: var(--p-primary-contrast-color);
  flex-shrink: 0;
}

.operator-meta {
  min-width: 0;
}

.last-login {
  white-space: nowrap;
}

/* Kebab affordance — always visible per the queue convention. The
   subtle dot column reads as "row has actions" without requiring a
   hover-reveal that hides functionality. */
.action-kebab {
  width: 32px;
  height: 32px;
  border-radius: var(--p-border-radius-sm);
  color: var(--p-text-muted-color);
  transition:
    background 160ms ease,
    color 160ms ease;
}

.action-kebab:hover {
  background: rgba(15, 23, 42, 0.04);
  color: var(--p-text-color);
}

.empty {
  padding: 3rem 1rem;
}
</style>
